import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Input, IconButton, Text, Image, Link } from '@chakra-ui/react';
import { ArrowForwardIcon, CheckIcon, AttachmentIcon, CloseIcon, PhoneIcon } from '@chakra-ui/icons';
import socketIOClient from 'socket.io-client';
import axios from 'axios';

const Chat = ({ jobId }) => {
  const socket = useRef(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState('');
  const [room, setRoom] = useState('defaultRoom'); // Set a default room value
  const [isPermitted, setIsPermitted] = useState(true);
  const [senderFullName, setSenderFullName] = useState('');
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageSuggestions = ["Good morning", "Hi", "Hello", "How are you?"];

  useEffect(() => {
    const checkPermissionAndFetchMessages = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/chat/check-permission`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('ticketToken')}`
          }
        });
        if (response.data.permitted === false) {
          setIsPermitted(false);
        } else {
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/chat/get-FullName`, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('ticketToken')}`
            }
          });
          console.log('response', response);
          sessionStorage.setItem('senderFullName', response.data);
          console.log('senderFullName', sessionStorage.getItem('senderFullName'));
          setIsPermitted(true);
          // Fetch messages if permitted
          const messagesResponse = await axios.post(`${process.env.REACT_APP_SERVER_URL}/chat/get-messages`,
            { chatRoomId: jobId },
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem('ticketToken')}`
              }
            }
          );
          if (messagesResponse.data.status === 'ok') {
            const formattedMessages = messagesResponse.data.response.map(msg => ({
              text: msg.messages.message,
              sender: msg.messages.isCurrentUser ? '' : msg.senderDetails.FullName, // Remove 'Me' for sent messages
              timestamp: new Date(msg.messages.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              file: msg.messages.attachments.fileUrl ? { url: msg.messages.attachments.fileUrl } : null,
              isCurrentUser: msg.messages.isCurrentUser
            }));
            setMessages(formattedMessages);
          }
        }
      } catch (error) {
        console.error("Error checking permission or fetching messages:", error);
      }
    };

    checkPermissionAndFetchMessages();
  }, []);

  useEffect(() => {
    if (!isPermitted) return;
    console.log('senderFullName', senderFullName);
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    console.log('serverUrl', serverUrl);
    socket.current = socketIOClient(serverUrl);
    console.log("jobId", jobId);
    const handleReceivedMessage = (message) => {
      console.log('message', message);
      const newMessage = {
        text: message.message,
        sender: message.senderFullName,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    };

    socket.current.on('recieved_message', handleReceivedMessage);

    return () => {
      socket.current.off('recieved_message', handleReceivedMessage);
      socket.current.disconnect();
    };
  }, [isPermitted, jobId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const joinRoom = () => {
    if (room !== '') {
      console.log(`Joining room: ${room}`);
      socket.current.emit('join_room', room);
      localStorage.setItem('room', room);
    }
  };

  useEffect(() => {
    joinRoom();
  }, [room]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSendMessage = async () => {
    if (message.trim() || selectedFile) {
      const senderFullName = sessionStorage.getItem('senderFullName'); // Assuming you store the user's full name in sessionStorage
      const newMessage = {
        text: message,
        file: selectedFile,
        sender: '', // No sender name for sent messages
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isCurrentUser: true // Ensure this is set to true for sent messages
      };

      setMessages([...messages, newMessage]);
      setMessage('');
      setSelectedFile(null);

      console.log(`Sending message: ${message} to room: ${room}`);
      socket.current.emit('send_message', { message,senderFullName, room });

      try {
        const ticketToken = sessionStorage.getItem('ticketToken');
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/chat/send-message`,
          {
            message: newMessage.text,
            chatRoomId: jobId,
            status: "sent",
            fileType: newMessage.file ? newMessage.file.type : null,
            fileUrl: newMessage.file ? " " : null
          },
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        if (response.status === 200) {
          console.log("Message sent successfully:");
        } else {
          console.log("Message sent failed:");
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    handleSendMessage();
  };

  const handleFileAttachment = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setMessage(suggestion);
  };

  const renderMessageText = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <Link key={index} href={part} isExternal color="blue">
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  if (!isPermitted) {
    return (
      <Flex align="center" justify="center" height="100vh">
        <Text fontSize="xl" color="red.500">You have not been permitted to access this chat.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" height="100vh" overflow="hidden">
      {/* Header */}
      <Flex bg="blue.500" p={4} color="white" align="center" justify="space-between">
        <Text fontSize="lg" fontWeight="bold">Chat</Text>
        <Flex align="center">
          <IconButton
            colorScheme="blue"
            aria-label="Join Room"
            icon={<CheckIcon />}
            isRound
            onClick={joinRoom}
            mr={2}
          />
          <IconButton
            colorScheme="blue"
            aria-label="Call"
            icon={<PhoneIcon />}
            isRound
            onClick={() => alert('Calling...')}
          />
        </Flex>
      </Flex>

      {/* Messages Area */}
      <Flex
        direction="column"
        flex="1"
        p={4}
        overflowY="auto"
        bg="gray.100"
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none'
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            bg={message.isCurrentUser ? 'blue.500' : 'white'}
            color={message.isCurrentUser ? 'white' : 'black'}
            p={3}
            borderRadius="lg"
            mb={2}
            maxWidth="70%"
            alignSelf={message.isCurrentUser ? 'flex-end' : 'flex-start'}
            boxShadow="md"
          >
            {message.sender && (
              <Text
                fontWeight="bold"
                mb={1}
                fontSize="sm"
                color={message.isCurrentUser ? 'blue.300' : 'gray.500'}
              >
                {message.sender}
              </Text>
            )}
            {message.file && (
              <Box mb={2}>
                <Link href={message.file.url} isExternal fontSize="sm" fontWeight="bold">
                  {message.file.url}
                </Link>
              </Box>
            )}
            <Flex justify="space-between" align="center">
              <Text flex="1">{renderMessageText(message.text)}</Text>
              <Flex align="center" ml={2}>
                <Text fontSize="xs" color="gray.300" mr={1}>{message.timestamp}</Text>
                {message.isCurrentUser && <CheckIcon color="gray.300" />}
              </Flex>
            </Flex>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Flex>

      {/* Message Suggestions */}
      {inputValue === '' && (
        <Flex p={2} justify="center" wrap="wrap" bg="gray.100">
          {messageSuggestions.map((suggestion, index) => (
            <Box
              key={index}
              m={1}
              p={2}
              bg="white"
              borderRadius="md"
              boxShadow="sm"
              cursor="pointer"
              _hover={{ bg: 'gray.200' }}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <Text fontSize="sm" color="black">{suggestion}</Text>
            </Box>
          ))}
        </Flex>
      )}

      {/* File Preview */}
      {selectedFile && (
        <Flex p={2} bg="gray.200" align="center" justify="space-between" borderTop="1px solid" borderColor="gray.300">
          {selectedFile.type.startsWith('image/') ? (
            <Image src={URL.createObjectURL(selectedFile)} alt={selectedFile.name} maxH="50px" borderRadius="md" />
          ) : (
            <Text fontSize="sm">{selectedFile.name}</Text>
          )}
          <IconButton
            colorScheme="red"
            aria-label="Remove file"
            icon={<CloseIcon />}
            onClick={() => setSelectedFile(null)}
            isRound
            size="sm"
          />
        </Flex>
      )}

      {/* Input Area */}
      <Flex p={4} bg="gray.100" borderTop="1px solid" borderColor="gray.200" align="center">
        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flex: '1' }}>
          <Input
            ref={inputRef}
            placeholder="Type a message..."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setInputValue(e.target.value);
            }}
            flex="1"
            mr={2}
            height="50px"
            border="none"
            borderRadius="full"
            boxShadow="md"
            bg="white"
            _placeholder={{ color: 'gray.500' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            p={2}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <IconButton
            colorScheme="blue"
            aria-label="Attach file"
            icon={<AttachmentIcon />}
            onClick={handleFileAttachment}
            isRound
            mr={2}
          />
          <IconButton
            type="submit"
            colorScheme="blue"
            aria-label="Send message"
            icon={<ArrowForwardIcon />}
            isRound
          />
        </form>
      </Flex>
    </Flex>
  );
};

export default Chat;